/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import type {
  AccessOverridesStorageObject,
  FeatureFlagOptions,
  FlattenedFeatureFlag,
  MultipleProjectFeatureFlags,
  OverridesStorageContext
} from '../models/LaunchDarkly';
import type {CoreContext} from '../models/Context';
import {
  FeatureFlag,
  FeatureFlagPayload,
  GetMultiProjectFeatureFlagsResponse,
  GraphQLErrorInfo,
  LaunchDarklyFeatureFlagsResponse,
  LdInput,
  MultiFeatureFlagUser,
  ProjectFeatureFlags
} from '@exc/graphql/src/models/launchDarkly';
import {generateUserHash, staleWhileRevalidate} from '../utils';
import {getMultiProjectFeatureFlags, launchDarklyFeatureFlags} from '@exc/graphql/src/queries/launchDarkly';
import {mergeFeatureFlagsForShell} from '../modules/DebugModules/AccessOverrides/common';
import metrics, {Level} from '@adobe/exc-app/metrics';
import {ORG_ONLY_PROJECTS, ORG_SANDBOX_PROJECTS, VALID_LAUNCH_DARKLY_PROJECTS} from '../utils/launchDarkly';
import {storage} from '@exc/storage';
import unifiedShellSession from './UnifiedShellSessionService';

export default class LaunchDarklyService {
  private readonly promises: Record<string, Promise<MultipleProjectFeatureFlags|undefined>> = {};
  private readonly metrics = metrics.create('exc.core.services.LaunchDarklyService');
  private readonly fetched = new Set;

  /**
   * Creates a key that includes project specific key info as well as the project id
   * @param {string} key - A string including the required user key info (org or org/sandbox)
   * @param {string} id - Project id to identify which projects have been fetched with which contexts
   * @returns {string} - A concatenated string including all the necessary key indicators
   * @private
   */
  private createKey = (key: string, id: string): string => `${key}/${id}`;

  /**
   * Cleans up the array of projects' features flags.
   * @param {ProjectFeatureFlags[]} data - Array of feature flag objects
   * @returns {MultipleProjectFeatureFlags} - A flattened object of projects with feature flag properties
   * @private
   */
  private flattenFeatureFlags(data: ProjectFeatureFlags[]): MultipleProjectFeatureFlags {
    const flattenedFlags: MultipleProjectFeatureFlags = {};
    // Go through each project's array of feature flags and set as objects
    data.forEach(({projectId, featureFlags}: ProjectFeatureFlags) => {
      flattenedFlags[projectId] = {};
      featureFlags.forEach(({name, value}: FeatureFlag) => {
        flattenedFlags[projectId][name] = value;
      });
    });
    // Returned a flattened object
    return flattenedFlags;
  }

  /**
   * Merges feature flag response data with overrides for development and testing
   * @param {ProjectFeatureFlags[]} projectFeatureFlags - Response of feature flags by project id
   * @param {string} hashedAuthId - User hash string used in storage of override values
   * @returns {MultipleProjectFeatureFlags} - A flattened object of projects with feature flag properties
   * @private
   */
  private async overrideFeatureFlags(
    projectFeatureFlags: MultipleProjectFeatureFlags,
    hashedAuthId: string,
    overrides?: AccessOverridesStorageObject
  ): Promise<MultipleProjectFeatureFlags> {
    const storedData = overrides || await storage.local.get('accessOverrides') as AccessOverridesStorageObject;
    const featureFlags = storedData?.[hashedAuthId]?.['featureFlags'] || {};
    const mergedFlags = {...projectFeatureFlags};
    for (const [key, value] of Object.entries(featureFlags)) {
      mergedFlags[key] = mergeFeatureFlagsForShell(value, projectFeatureFlags[key]) as FlattenedFeatureFlag;
    }
    return mergedFlags;
  }

  /**
   * Cleans up and sorts response for multiple flag requests
   * @param {Array} projectIds - Array of LaunchDarkly project IDs
   * @param {Object} projectFeatureFlags - Response of feature flags by project ID (possibly from cache)
   * @param {OverridesStorageContext} context - Core context values needed to deliver the data
   * @returns {MultipleProjectFeatureFlags} - An object of project ids with flattened feature flags
   * @private
   */
  private async formatResponse(
    projectIds: string[],
    projectFeatureFlags: MultipleProjectFeatureFlags,
    context: OverridesStorageContext,
    overrides?: AccessOverridesStorageObject
  ): Promise<MultipleProjectFeatureFlags> {
    // Support internal feature flag overrides for runtime API
    const {accessOverridesEnabled, hashedAuthId} = context;
    // Limited to internal users and overrides set to on/enabled
    if (accessOverridesEnabled) {
      projectFeatureFlags = await this.overrideFeatureFlags(projectFeatureFlags, hashedAuthId, overrides);
    }
    // Because the response could include ALL the projects
    // We filter the list by just the requested projects first
    return Object.keys(projectFeatureFlags)
      .sort()
      .reduce((result: MultipleProjectFeatureFlags, key) => projectIds.includes(key) ? {...result, [key]: projectFeatureFlags[key]} : result, {});
  }

  /**
   * Validates the projects requested against those in a response.
   * @param {Array} projectIds - Array of LaunchDarkly project IDs
   * @param {Object} response - Response of feature flags by project ID
   * @returns {Array} Project ids for projects missing from the response
   * @private
   */
  private getUncachedProjects(projectIds: string[], response = {}): string[] {
    const cachedProjectKeys = Object.keys(response);
    return projectIds.filter((key: string) => !cachedProjectKeys.includes(key));
  }

  /**
   * Validates the projects requested against which projects have been fetched this session.
   * @param {Array} projectIds - Array of LaunchDarkly project IDs
   * @param {String} cacheKey - A string of response keys (org, org+sandbox)
   * @returns {Array} Project ids for projects that have not been fetched this session
   * @private
   */
  private getUnfetchedProjects = (projectIds: string[], cacheKey: string): string[] =>
    projectIds.filter((id: string) => !this.fetched.has(this.createKey(cacheKey, id)));

  /**
   * Cleans up the response, caches and returns it.
   * @param {string} key - unique key for type of feature flag query
   * @param {boolean} isUnifiedShell - denotes unified shell only flags
   * @param {GraphQlResponse} graphQLResponse - Multiple project response from GraphQl
   * @param {string} projectId - Requests for one project have an id, defaults to ''
   * @returns {MultipleProjectFeatureFlags} - An object of project ids with flattened feature flags
   * @private
   */
  private onSuccess(
    key: string,
    graphQLResponse: GetMultiProjectFeatureFlagsResponse | LaunchDarklyFeatureFlagsResponse,
    isUnifiedShell = false,
    projectIds: string[]
  ): MultipleProjectFeatureFlags {
    // When a successful request is made to get the latest data - Add the key/projectId
    // pair to the fetched set so subsequent requests do not make additional requests
    projectIds.forEach(id => this.fetched.add(this.createKey(key, id)));
    let flattenedFlags: MultipleProjectFeatureFlags;
    // GraphlQL handles the requests for unified shell feature flags
    // separately from the bulk requests to protect performance.
    // Unified shell flags are returned in an object:
    // {featureFlags: {
    //   featureFlags: [{name: 'name', value: 'value'}]
    //   user: {}
    // }}
    // Whereas the bulk returns an object:
    // {multiProjectFeatureFlags: {
    //    projects: [
    //     {projectId: 'id', featureFlags: {name: 'name', value: 'value'}},
    //     {projectId: 'id', featureFlags: {name: 'name', value: 'value'}}
    // ]}}
    if (isUnifiedShell) {
      const {featureFlags: {featureFlags}} = graphQLResponse as LaunchDarklyFeatureFlagsResponse;
      flattenedFlags = this.flattenFeatureFlags([{featureFlags, projectId: projectIds[0]}]);
    } else {
      const {multiProjectFeatureFlags: {projects}} = graphQLResponse as GetMultiProjectFeatureFlagsResponse;
      flattenedFlags = this.flattenFeatureFlags(projects);
    }
    // Add to the cache and log event
    unifiedShellSession.update('featureFlags', flattenedFlags, null, key, false);
    this.metrics.event('LaunchDarklyService.cacheUpdate', {key});
    // Return the flags sorted by project so we're handling similar object shapes
    return flattenedFlags;
  }

  /**
   * Returns an existing, cached response for multiple project feature flags.
   * Looks at the cache first, then at pending requests.
   * @param {string} cacheKey - unique key (imsOrgId or imsOrgId/sandbox)
   * @returns {MultipleProjectFeatureFlags} - An object of project ids with flattened feature flags
   * @private
   */
  private async getExistingResponse(cacheKey: string): Promise<MultipleProjectFeatureFlags|undefined> {
    try {
      // Look for existing result (or pending result) in memory
      const existingPromise = this.promises[cacheKey];
      // Look for cached result
      const response = await unifiedShellSession.get('featureFlags', null, cacheKey);
      // If it's not cached, use a pending response if we have one.
      return response || existingPromise;
    } catch (err) {
      this.metrics.warn('Failed to get LaunchDarkly response from cache or memory', {err});
    }
  }

  /**
   * Checks context data to see if the required data is missing.
   * @param {Array} projectIds - LaunchDarkly project ids strings
   * @param {CoreContext} context - Core Context
   * @returns {boolean} False if context has all data needed, true otherwise.
   */
  public isContextMissing(projectIds: string[], context: CoreContext): boolean {
    const {imsOrg, internal, locale, sandbox} = context;
    // We want to check that internal is defined (not just false with !internal)
    // Our LaunchDarkly payload requires the following values
    if (!imsOrg || typeof internal !== 'boolean' || !locale) {
      return true;
    }
    // Some LaunchDarkly requests require a sandbox name string
    const sandboxRequired = projectIds.some(project => ORG_SANDBOX_PROJECTS.includes(project));
    return sandboxRequired && !sandbox;
  }

  /**
   * A wrapping function to support the bulk API with a list of project ids
   * as well as clean up and sort the returned object
   * @param {Array} projectIds - Array of strings of project IDs or ['*'] for all
   * @param {CoreContext} context - Current context object that includes options we need
   * @returns {MultipleProjectFeatureFlags} - An object of project ids with flattened feature flags
   * @private
   */
  public async getByProjects(
    projectIds: string[],
    context: CoreContext,
    overrides?: AccessOverridesStorageObject
  ): Promise<MultipleProjectFeatureFlags|undefined> {
    const {
      accessOverridesEnabled,
      imsOrg: imsOrgId,
      hashedAuthId,
      internal,
      locale,
      imsInfo: {imsProfile},
      sandbox
    } = context;
    // Support bulk API with ['*'] request
    const fetchAll = projectIds.length === 1 && projectIds[0] === '*';
    if (fetchAll) {
      // We need the projectIds for clean up at the end
      projectIds = VALID_LAUNCH_DARKLY_PROJECTS;
    }
    // Construct the graphql query options
    const options: FeatureFlagOptions = {
      imsOrgId,
      internal,
      locale,
      sandboxName: sandbox?.name
    };
    // Add the email to the options if it set.
    imsProfile?.email && (options.userHash = generateUserHash(imsProfile.email));
    // Set up the requested projects by key type
    const orgOnlyProjects = fetchAll ? ORG_ONLY_PROJECTS : projectIds.filter(project => ORG_ONLY_PROJECTS.includes(project));
    const orgSandboxProjects = fetchAll ? ORG_SANDBOX_PROJECTS : projectIds.filter(project => ORG_SANDBOX_PROJECTS.includes(project));
    // Some projects require the org AND sandbox in the request key (i.e. CJM)
    // So we separate the requests by org only and org/sandbox requests
    const [orgOnlyFlags, orgSandboxFlags] = await Promise.all([
      orgOnlyProjects && this.getBulkData(orgOnlyProjects, options),
      orgSandboxProjects && this.getBulkData(orgSandboxProjects, options, `${options.imsOrgId}/${options.sandboxName}`)
    ]);
    // Clean up and sort the response object
    return this.formatResponse(
      projectIds,
      {...orgOnlyFlags, ...orgSandboxFlags},
      {accessOverridesEnabled, hashedAuthId},
      overrides
    );
  }

  /**
   * Gets multiple project feature flags from LaunchDarkly response from cache or GraphQL.
   * @param {Array} projectIds - Array of allowed LaunchDarkly project IDs
   * @param {FeatureFlagOptions} options - Context options to properly fetch data
   * @param {string?} customKeyValue - Custom key value for storage (i.s. org + sandbox)
   * @returns {FeatureFlagOptions} - An object of project ids with flattened feature flags
   */
  async getBulkData(projectIds: string[], options: FeatureFlagOptions, customKeyValue?: string): Promise<MultipleProjectFeatureFlags|undefined> {
    // If there are no id for this request from getByProjects
    if (!projectIds.length) {
      return undefined;
    }
    const {imsOrgId} = options;
    // Validate the request includes only valid projectIDs support by GraphQL
    if (!projectIds.every(projectId => VALID_LAUNCH_DARKLY_PROJECTS.includes(projectId))) {
      const message = 'Launch Darkly Service Error: invalid projectID';
      this.metrics.event('LaunchDarklyService.validationError', {message, projectIds}, {level: Level.ERROR});
      throw new Error(`${message} - ${projectIds}`);
    }
    // Check for an existing response in memory or cache
    const cacheKey = customKeyValue || imsOrgId;
    const response = await this.getExistingResponse(cacheKey);
    // Validate that the response has all the data that's been requested
    const missingProjects = this.getUncachedProjects(projectIds, response) || [];
    // Validate requested flags are fetched at least once per page load
    const unfetchedProjects = this.getUnfetchedProjects(projectIds, cacheKey) || [];
    // We don't need to go through the staleWhileRevalidate workflow
    // every single time. If we have a fully validated response, return it
    if (response && !missingProjects.length && !unfetchedProjects.length) {
      return response;
    }
    // If the cache or promise response is missing data, don't return it
    // It could be an empty object when the API fetches it
    // We reassign `response` so we don't return an empty/partial response
    const validatedResponse = !missingProjects.length ? response : undefined;
    // Construct the promise to fetch, process, cache, and return the response
    const userData: MultiFeatureFlagUser<FeatureFlagPayload> = {
      custom: options
    };
    // Construct a list of projects to fetch in the graphql promise
    // including missing projects and those unfetched this session
    projectIds = Array.from(new Set(missingProjects.concat(unfetchedProjects)));
    const getGraphQLPromise = async (): Promise<MultipleProjectFeatureFlags|undefined> => {
      const graphQLPromise = getMultiProjectFeatureFlags({projectIds, userData})
        .then((graphQLResponse: GetMultiProjectFeatureFlagsResponse) => {
          const {errors} = graphQLResponse;
          // GraphQl hands back an array of santized errors
          if (errors) {
            this.onError(cacheKey, errors);
            return {};
          }
          const successResponse = this.onSuccess(cacheKey, graphQLResponse, false, projectIds);
          // Since we filter the list of projects fetched to reduce unnecessary calls to graphql
          // We need to hydrate the response with cache + newly fetched flags
          return {...response, ...successResponse};
        })
        .finally(() => delete this.promises[cacheKey]);
      this.promises[cacheKey] = graphQLPromise;
      return graphQLPromise;
    };
    return staleWhileRevalidate<MultipleProjectFeatureFlags|undefined>(getGraphQLPromise, validatedResponse);
  }

  /**
   * Gets Unified Shell feature flags from LaunchDarkly response from cache or GraphQL.
   * @param {string} projectId - LaunchDarkly project ID
   * @param {Object} options - Options like user data that GraphQl needs
   * @returns {MultipleProjectFeatureFlags} - An object of project ids with flattened feature flags
   */
  async getFeatureFlags(projectId: string, options: FeatureFlagOptions): Promise<MultipleProjectFeatureFlags|undefined> {
    const {imsOrgId} = options;
    // Check for an existing response in memory or cache
    let response = await this.getExistingResponse(imsOrgId);
    // Validate the response or fetch a fresh response.
    // We need to handle the storage change from the response being: {flag: 'value'}
    // to the response now being: {'unified-shell': {flag: 'value'}}
    response = response && this.getUncachedProjects([projectId], response) ? response : undefined;
    // We don't need to go through the staleWhileRevalidate workflow
    // every single time. Right now, we just want to check if the imsOrg
    // has been fetched this session and return the cached response.
    if (this.fetched.has(this.createKey(imsOrgId, projectId)) && response) {
      return response;
    }
    // Remove the userHash value if it's undefined.
    options.userHash || delete options.userHash;
    // Construct the promise to fetch, cache, and return the response
    const userData: LdInput<FeatureFlagPayload> = {
      userData: {
        custom: options,
        key: imsOrgId
      }
    };
    const getGraphQLPromise = async (): Promise<MultipleProjectFeatureFlags|undefined> => {
      const graphQLPromise = launchDarklyFeatureFlags(userData)
        .then((graphQLResponse: LaunchDarklyFeatureFlagsResponse) => {
          const {errors} = graphQLResponse;
          // GraphQl hands back an array of santized errors
          if (errors) {
            this.onError(imsOrgId, errors);
            return {};
          }
          return this.onSuccess(imsOrgId, graphQLResponse, true, [projectId]);
        })
        .finally(() => delete this.promises[imsOrgId]);
      this.promises[imsOrgId] = graphQLPromise;
      return graphQLPromise;
    };
    return staleWhileRevalidate<MultipleProjectFeatureFlags|undefined>(getGraphQLPromise, response);
  }

  /**
   * Logs an error for background, non-blocking GraphQL failures.
   * @param {string} key - String with type and currently selected ims org
   * @param {array} errors - array or graphql errors
   * @private
   */
  private onError(key: string, errors: GraphQLErrorInfo[]): void {
    // The request failed for this imsOrg, so don't cache that response in the
    // fetched set. Now subsequent requests will re-fetch.
    this.fetched.delete(key);
    const message = `GQL failed to fetch Launch Darkly feature flags. ${errors.map(err => err.message).join(', ')}` || 'Unknown error';
    this.metrics.event('LaunchDarklyService.backgroundError', {message}, {level: Level.ERROR});
  }
}
